import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import PageHero from "@/components/PageHero";
import EditorialMarquee from "@/components/EditorialMarquee";
import { Reveal, Eyebrow } from "@/components/Motion";
import { EXPERTISE, PROCEDURES, IMAGES } from "@/data/content";
import { CONDITIONS } from "@/data/conditions";
import { useLang } from "@/i18n/LanguageContext";
import { useContent } from "@/i18n/useContent";

const Treatments = () => {
  const { t, lang } = useLang();
  const { expertise: EXPERTISE_L, conditions: CONDITIONS_L } = useContent();
  return (
  <main data-testid="treatments-page">
    <PageHero
      key={lang}
      eyebrow={t("tr_hero_eyebrow")}
      lines={[t("tr_hero_l1"), t("tr_hero_l2")]}
      testId="treatments-hero"
    >
      {t("tr_hero_sub")}
    </PageHero>

    {/* NUMBERED MANIFESTO CHAPTERS */}
    <section data-testid="expertise-chapters" className="bg-bone py-16 lg:py-24">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        {EXPERTISE_L.map((e, i) => (
          <Reveal key={e.title} delay={0.05}>
            <article
              data-testid={`expertise-chapter-${i}`}
              className="group grid grid-cols-1 lg:grid-cols-12 gap-4 lg:gap-10 py-12 lg:py-14 border-t border-pine-900/15 first:border-t-0"
            >
              <div className="lg:col-span-2">
                <span className="font-serif italic text-6xl lg:text-7xl text-pine-200 group-hover:text-pine-500 transition-colors duration-500">
                  {String(i + 1).padStart(2, "0")}
                </span>
              </div>
              <div className="lg:col-span-6">
                <h2 className="font-serif font-light text-3xl sm:text-4xl tracking-tight text-pine-950 leading-tight">
                  {e.title}
                </h2>
                <p className="mt-4 text-sage leading-relaxed max-w-xl">{e.description}</p>
                <Link
                  to={`/conditions/${CONDITIONS[i]?.slug}`}
                  data-testid={`chapter-guide-link-${i}`}
                  className="group/link mt-4 inline-flex items-center gap-2 text-sm font-semibold text-pine-700 hover:text-pine-500 transition-colors duration-300"
                >
                  {t("tr_guide_link")}
                  <ArrowRight size={14} className="transition-transform duration-300 group-hover/link:translate-x-1" />
                </Link>
              </div>
              <div className="lg:col-span-4 flex flex-wrap lg:justify-end content-start gap-2">
                {e.tags.map((tag) => (
                  <span
                    key={tag}
                    className="text-[11px] uppercase tracking-[0.14em] font-semibold text-pine-700 bg-pine-100/70 border border-pine-200 px-3.5 py-1.5 rounded-full"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </article>
          </Reveal>
        ))}
      </div>
    </section>

    <EditorialMarquee dark />

    {/* PROCEDURES BENTO */}
    <section data-testid="procedures-section" className="bg-white py-24 lg:py-32">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <Reveal>
          <Eyebrow>{t("tr_proc_eyebrow")}</Eyebrow>
          <h2 className="mt-6 font-serif font-light text-4xl sm:text-5xl tracking-tight text-pine-950 leading-[1.05]">
            {t("tr_proc_title1")} <span className="italic text-pine-500">{t("tr_proc_title2")}</span>
          </h2>
        </Reveal>

        <div className="mt-14 grid grid-cols-1 lg:grid-cols-12 gap-6">
          <Reveal className="lg:col-span-4">
            <div className="relative overflow-hidden h-full min-h-[22rem] group">
              <img
                src={IMAGES.sculpture}
                alt="Sculpture — a metaphor for surgical restoration"
                data-testid="procedures-sculpture-image"
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-pine-950/80 to-transparent" />
              <div className="absolute bottom-0 p-8">
                <div className="font-serif italic text-2xl text-white leading-snug">
                  {t("tr_proc_quote")}
                </div>
              </div>
            </div>
          </Reveal>

          <div className="lg:col-span-8 grid grid-cols-1 sm:grid-cols-2 gap-px bg-mist border border-mist">
            {[t("pr1"), t("pr2"), t("pr3"), t("pr4"), t("pr5"), t("pr6"), t("pr7"), t("pr8")].map((p, i) => (
              <Reveal key={p} delay={0.05 * i} className="h-full">
                <div
                  data-testid={`procedure-${i}`}
                  className="bg-white h-full p-7 flex items-center justify-between gap-4 group hover:bg-pine-50 transition-colors duration-500"
                >
                  <span className="font-medium text-pine-900">{p}</span>
                  <ArrowRight
                    size={16}
                    className="shrink-0 text-pine-300 group-hover:text-pine-500 group-hover:translate-x-1 transition-[color,transform] duration-300"
                  />
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>

    {/* CTA */}
    <section data-testid="treatments-cta" className="bg-bone border-t border-mist py-20 lg:py-28">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 flex flex-col lg:flex-row lg:items-end justify-between gap-8">
        <Reveal>
          <h2 className="font-serif font-light text-4xl sm:text-5xl tracking-tight text-pine-950 leading-[1.05]">
            {t("tr_cta_title1")}<br />{t("tr_cta_title2")}
          </h2>
          <p className="mt-4 text-sage max-w-xl">
            {t("tr_cta_sub")}
          </p>
        </Reveal>
        <Reveal delay={0.15}>
          <Link
            to="/book"
            data-testid="treatments-book-button"
            className="group inline-flex items-center gap-2 bg-pine text-bone font-semibold text-sm px-8 py-4 rounded-full hover:bg-pine-500 transition-colors duration-300"
          >
            {t("tr_cta_btn")}
            <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </Reveal>
      </div>
    </section>
  </main>
  );
};

export default Treatments;
